import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Upload, FileWarning, ShieldAlert, FlaskConical, Plus, CheckCircle2, XCircle, Download, PencilLine, Ban, Undo2, BookOpen } from "lucide-react";
import { EditDialog, RetireDialog } from "@/components/RecordEditor";
import { useFileViewer } from "@/components/FileViewer";

const LOT_BADGE = {
  Received: "bg-slate-200 text-slate-800",
  "In use": "bg-[#134e4a] text-white",
  Expired: "bg-red-800 text-white",
  Depleted: "bg-slate-300 text-slate-700",
  Quarantined: "bg-red-100 text-red-900",
};

const Section = ({ title, children, testid, action }) => (
  <section className="border border-slate-200" data-testid={testid}>
    <header className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
      <h2 className="text-base font-semibold text-slate-800 md:text-lg">{title}</h2>
      {action && <div className="ml-auto">{action}</div>}
    </header>
    <div className="p-3">{children}</div>
  </section>
);

const EMPTY_LOT = { lot_number: "", expiry_date: "", quantity_received: "", location: "", notes: "" };
const EMPTY_QC = { qc_type: "New-lot validation", control_used: "", expected_result: "", observed_result: "", passed: true, comments: "" };

export default function ReagentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [item, setItem] = useState(null);
  const [lotForm, setLotForm] = useState({ ...EMPTY_LOT });
  const [qcLot, setQcLot] = useState(null);
  const [qcForm, setQcForm] = useState({ ...EMPTY_QC });
  const [sdsFile, setSdsFile] = useState(null);
  const [sdsRev, setSdsRev] = useState("");
  const [busy, setBusy] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [editLot, setEditLot] = useState(null);
  const [voidLot, setVoidLot] = useState(null);
  const [showRetiredLots, setShowRetiredLots] = useState(false);
  const { openFile, viewerElement } = useFileViewer();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/reagents/${id}`);
      setItem(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [id]);

  useEffect(() => { api.get("/reagents/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  if (!item) return <div className="text-sm text-slate-500" data-testid="reagent-detail-loading">Loading reagent…</div>;
  const canManage = ["admin", "lab_director", "supervisor", "pathologist", "pathologist_assistant", "tech"].includes(user?.role);
  const canQuarantine = ["admin", "lab_director", "supervisor", "pathologist"].includes(user?.role);
  const canBench = ["admin", "lab_director", "supervisor", "pathologist", "pathologist_assistant", "tech"].includes(user?.role);

  const receiveLot = async (e) => {
    e.preventDefault();
    setBusy("lot");
    try {
      await api.post(`/reagents/${id}/lots`, {
        ...lotForm, quantity_received: Number(lotForm.quantity_received),
      });
      toast.success(`Lot ${lotForm.lot_number} received`);
      setLotForm({ ...EMPTY_LOT });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const setStatus = async (lot, status) => {
    const reason = status === "Quarantined"
      ? window.prompt(`Why are you quarantining lot ${lot.lot_number}?`) || ""
      : "";
    if (status === "Quarantined" && !reason.trim()) return;
    setBusy(lot.id);
    try {
      await api.post(`/reagent-lots/${lot.id}/status`, { status, reason });
      toast.success(`Lot ${lot.lot_number} → ${status}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const saveQc = async (e) => {
    e.preventDefault();
    setBusy("qc");
    try {
      const { data } = await api.post(`/reagent-lots/${qcLot.id}/qc`, qcForm);
      toast[qcForm.passed ? "success" : "error"](
        qcForm.passed ? "QC recorded as passed" : "QC failed — lot quarantined");
      setQcLot(null);
      setQcForm({ ...EMPTY_QC });
      void data;
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const uploadSds = async (e) => {
    e.preventDefault();
    if (!sdsFile) return toast.error("Choose the SDS file first");
    setBusy("sds");
    const body = new FormData();
    body.append("file", sdsFile);
    try {
      await api.post(`/reagents/${id}/sds?revision_date=${encodeURIComponent(sdsRev)}`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Safety data sheet uploaded");
      setSdsFile(null);
      setSdsRev("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const openSds = () => openFile(`/reagents/${id}/sds`, {
    filename: item?.sds_filename || "sds.pdf",
    title: `${item?.name || "Reagent"} — safety data sheet`,
  });

  return (
    <div className="space-y-6" data-testid="reagent-detail-page">
      <Link to="/reagents" className="inline-flex items-center gap-2 text-sm font-semibold text-[#134e4a] hover:underline" data-testid="back-to-reagents">
        <ArrowLeft size={15} /> All reagents
      </Link>

      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900" data-testid="reagent-name">{item.name}</h1>
          <p className="mt-1 text-sm text-slate-600">
            {item.manufacturer || "No manufacturer on file"}
            {item.catalog_number ? ` · Cat ${item.catalog_number}` : ""} · {item.category} · {item.department}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {item.storage} · {item.hazard_class} · Reorder at {item.reorder_level} {item.unit}
            {item.qc_interval_days ? ` · Recurring QC every ${item.qc_interval_days} days` : ""}
          </p>
        </div>
        <div className="ml-auto text-right">
          <div className={`num text-3xl font-bold ${item.low_stock ? "text-amber-700" : "text-slate-900"}`} data-testid="reagent-on-hand">
            {item.quantity_on_hand} {item.unit}
          </div>
          <div className="text-xs uppercase tracking-wide text-slate-500">on hand · {item.active_lot_count} active lots</div>
          {item.low_stock && <div className="mt-1 bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-900" data-testid="reagent-low-stock-badge">Low stock — reorder</div>}
          {canManage && (
            <div className="mt-2 flex flex-wrap justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}
                      data-testid="correct-reagent-button" className="h-9 border-slate-300">
                <PencilLine size={14} className="mr-1" /> Correct details
              </Button>
              {item.archived ? (
                <Button size="sm" variant="outline" data-testid="restore-reagent-button" className="h-9 border-slate-300"
                        onClick={async () => {
                          try {
                            await api.post(`/reagents/${id}/restore`);
                            toast.success("Reagent reinstated");
                            load();
                          } catch (e) { toast.error(apiError(e)); }
                        }}>
                  <Undo2 size={14} className="mr-1" /> Reinstate
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setRetireOpen(true)}
                        data-testid="retire-reagent-button" className="h-9 border-red-300 text-red-800 hover:bg-red-50">
                  <Ban size={14} className="mr-1" /> Retire reagent
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {item.archived && (
        <p className="border-l-2 border-slate-400 bg-slate-50 p-3 text-sm text-slate-700" data-testid="reagent-retired-note">
          Retired by {item.voided_by} on {fmtDateTime(item.voided_at)} — {item.void_reason}. It stays on file for inspectors under the Retired filter.
        </p>
      )}

      <Section title="Safety data sheet (SDS)" testid="reagent-sds">
        {item.has_sds ? (
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
            <Button variant="outline" onClick={openSds} data-testid="open-sds-button" className="h-10 border-slate-300">
              <BookOpen size={14} className="mr-2" /> Read {item.sds_filename}
            </Button>
            <Button variant="outline" data-testid="save-sds-button" className="h-10 border-slate-300"
                    onClick={() => openFile(`/reagents/${id}/sds`, { filename: item.sds_filename, forceSave: true })}>
              <Download size={14} />
            </Button>
            <span className="text-xs text-slate-500">
              SDS revision {item.sds_revision_date || "not recorded"} · uploaded by {item.sds_uploaded_by} {fmtDateTime(item.sds_uploaded_at)}
            </span>
          </div>
        ) : (
          <p className="flex items-center gap-2 text-sm font-semibold text-red-800" data-testid="reagent-no-sds">
            <FileWarning size={15} /> No safety data sheet on file — this is an inspection finding.
          </p>
        )}
        {canManage && (
          <form onSubmit={uploadSds} className="mt-3 flex flex-wrap items-end gap-3 border-t border-slate-100 pt-3">
            <div className="min-w-[220px] flex-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">SDS file (PDF or Word)</Label>
              <Input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setSdsFile(e.target.files?.[0] || null)}
                     data-testid="sds-file-input" className="mt-1 h-11 border-slate-300" />
            </div>
            <div className="w-44">
              <Label className="text-xs uppercase tracking-wide text-slate-600">SDS revision date</Label>
              <Input type="date" value={sdsRev} onChange={(e) => setSdsRev(e.target.value)}
                     data-testid="sds-revision-input" className="mt-1 h-11 border-slate-300" />
            </div>
            <Button type="submit" disabled={busy === "sds"} data-testid="upload-sds-button"
                    className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
              <Upload size={15} className="mr-2" />{busy === "sds" ? "Uploading…" : item.has_sds ? "Replace SDS" : "Upload SDS"}
            </Button>
          </form>
        )}
      </Section>

      <Section title="Consumption" testid="reagent-consumption">
        <p className="text-sm text-slate-700">
          Logged onto instruments: <span className="font-semibold text-slate-900">{item.consumed_display}</span>
          {item.change_count
            ? ` across ${item.change_count} reagent change${item.change_count === 1 ? "" : "s"}`
            : " — nothing logged yet"}
          {item.last_change_at ? ` · last ${fmtDateTime(item.last_change_at)}` : ""}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Totalled from the TechTraX reagent change logs, with millilitres, litres and gallons combined. Per-lot figures appear on
          each lot below.
          {item.consumed_unattributed_display && item.consumed_unattributed_display !== "0 mL"
            ? ` ${item.consumed_unattributed_display} was logged without a lot number, so it is not attributed to any lot.`
            : ""}
        </p>
      </Section>


      {canBench && (
        <Section title="Receive a lot" testid="reagent-receive-lot">
          <form onSubmit={receiveLot} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Lot number</Label>
              <Input value={lotForm.lot_number} onChange={(e) => setLotForm((f) => ({ ...f, lot_number: e.target.value }))}
                     data-testid="lot-number-input" required className="mt-1 h-11 border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Manufacturer expiry</Label>
              <Input type="date" value={lotForm.expiry_date} onChange={(e) => setLotForm((f) => ({ ...f, expiry_date: e.target.value }))}
                     data-testid="lot-expiry-input" required className="mt-1 h-11 border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Quantity ({item.unit})</Label>
              <Input type="number" min="0" step="any" value={lotForm.quantity_received}
                     onChange={(e) => setLotForm((f) => ({ ...f, quantity_received: e.target.value }))}
                     data-testid="lot-quantity-input" required className="mt-1 h-11 border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Storage location</Label>
              <Input value={lotForm.location} onChange={(e) => setLotForm((f) => ({ ...f, location: e.target.value }))}
                     data-testid="lot-location-input" placeholder="e.g. Fridge 2, shelf B" className="mt-1 h-11 border-slate-300" />
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={busy === "lot"} data-testid="receive-lot-button"
                      className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
                <Plus size={15} className="mr-2" />{busy === "lot" ? "Saving…" : "Receive"}
              </Button>
            </div>
          </form>
        </Section>
      )}

      <Section title={`Lots (${item.lot_count})`} testid="reagent-lots">
        {item.lots.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="reagent-lots-empty">No lots received yet.</p>
        ) : (
          <div className="space-y-3">
            {item.lots.map((lot) => (
              <div key={lot.id} className={`border p-3 ${lot.quarantined ? "border-red-300 bg-red-50/50" : lot.expired ? "border-red-200" : "border-slate-200"}`}
                   data-testid={`lot-${lot.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono font-semibold text-slate-900">Lot {lot.lot_number}</span>
                  <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${LOT_BADGE[lot.status]}`} data-testid={`lot-status-${lot.id}`}>{lot.status}</span>
                  {lot.expired && <span className="bg-red-800 px-1.5 py-0.5 text-[11px] font-bold text-white" data-testid={`lot-expired-${lot.id}`}>Expired</span>}
                  {lot.expiring_soon && <span className="bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">Expires in {lot.days_to_expiry}d</span>}
                  {lot.qc_overdue && <span className="bg-sky-100 px-1.5 py-0.5 text-[11px] font-semibold text-sky-900" data-testid={`lot-qc-overdue-${lot.id}`}>QC overdue</span>}
                  <span className="num ml-auto text-sm font-semibold text-slate-800">{lot.quantity_on_hand} / {lot.quantity_received} {item.unit}</span>
                </div>
                <p className="mt-1 text-xs text-slate-600">
                  Expires {fmtDateTime(lot.expiry_date)} · received {fmtDateTime(lot.received_date)} by {lot.received_by}
                  {lot.location ? ` · ${lot.location}` : ""}
                  {lot.opened_at ? ` · opened ${fmtDateTime(lot.opened_at)}` : ""}
                </p>
                {lot.quarantined && (
                  <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-red-900" data-testid={`lot-quarantine-note-${lot.id}`}>
                    <ShieldAlert size={15} /> Quarantined{lot.status_reason ? ` — ${lot.status_reason}` : ""}. Do not use on patient material.
                  </p>
                )}
                <p className="mt-2 text-xs text-slate-600">
                  QC records: {lot.qc_count}
                  {lot.last_qc_at ? ` · last ${fmtDateTime(lot.last_qc_at)} (${lot.last_qc_passed ? "pass" : "fail"})` : " · none yet"}
                  {item.requires_new_lot_qc && ` · new-lot validation ${lot.new_lot_qc_passed ? "passed" : "outstanding"}`}
                </p>
                <p className="mt-1 text-xs text-slate-600" data-testid={`lot-consumption-${lot.id}`}>
                  Used on instruments: <span className="font-semibold text-slate-800">{lot.consumed_display}</span>
                  {lot.change_count
                    ? ` across ${lot.change_count} change${lot.change_count === 1 ? "" : "s"}`
                    : " — no changes logged yet"}
                  {lot.instruments_used_on?.length ? ` · ${lot.instruments_used_on.join(", ")}` : ""}
                  {lot.stations_used?.length ? ` · station ${lot.stations_used.join(", ")}` : ""}
                  {lot.last_change_at ? ` · last ${fmtDateTime(lot.last_change_at)}` : ""}
                </p>
                {canBench && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditLot(lot)}
                            data-testid={`correct-lot-${lot.id}`} className="h-9 border-slate-300">
                      <PencilLine size={14} className="mr-1" /> Correct
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setVoidLot(lot)}
                            data-testid={`void-lot-${lot.id}`} className="h-9 border-red-300 text-red-800 hover:bg-red-50">
                      <Ban size={14} className="mr-1" /> Void lot
                    </Button>
                    <Button size="sm" onClick={() => { setQcLot(lot); setQcForm({ ...EMPTY_QC, qc_type: lot.new_lot_qc_passed ? "Daily" : "New-lot validation" }); }}
                            data-testid={`record-qc-${lot.id}`} className="h-9 bg-sky-800 hover:bg-sky-900">
                      <FlaskConical size={14} className="mr-1" /> Record QC
                    </Button>
                    {lot.status === "Received" && (
                      <Button size="sm" variant="outline" disabled={busy === lot.id} onClick={() => setStatus(lot, "In use")}
                              data-testid={`lot-in-use-${lot.id}`} className="h-9 border-slate-300">Put in use</Button>
                    )}
                    {["Received", "In use"].includes(lot.status) && (
                      <Button size="sm" variant="outline" disabled={busy === lot.id} onClick={() => setStatus(lot, "Depleted")}
                              data-testid={`lot-depleted-${lot.id}`} className="h-9 border-slate-300">Mark depleted</Button>
                    )}
                    {canQuarantine && lot.status !== "Quarantined" && (
                      <Button size="sm" variant="outline" disabled={busy === lot.id} onClick={() => setStatus(lot, "Quarantined")}
                              data-testid={`lot-quarantine-${lot.id}`} className="h-9 border-red-300 text-red-800 hover:bg-red-50">Quarantine</Button>
                    )}
                    {canQuarantine && lot.status === "Quarantined" && (
                      <Button size="sm" variant="outline" disabled={busy === lot.id} onClick={() => setStatus(lot, "Received")}
                              data-testid={`lot-release-${lot.id}`} className="h-9 border-slate-300">Release from quarantine</Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title={`QC history (${(item.qc_records || []).length})`} testid="reagent-qc-history">
        {(item.qc_records || []).length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="reagent-qc-empty">No QC recorded for this reagent yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="px-2 py-2">Date</th><th className="px-2 py-2">Lot</th><th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2">Control</th><th className="px-2 py-2">Expected / observed</th>
                  <th className="px-2 py-2">Result</th><th className="px-2 py-2">By</th>
                </tr>
              </thead>
              <tbody>
                {item.qc_records.map((q) => (
                  <tr key={q.id} className="border-b border-slate-100" data-testid={`qc-row-${q.id}`}>
                    <td className="px-2 py-2 text-xs text-slate-600">{fmtDateTime(q.performed_on)}</td>
                    <td className="px-2 py-2 font-mono text-xs">{q.lot_number}</td>
                    <td className="px-2 py-2 text-slate-700">{q.qc_type}</td>
                    <td className="px-2 py-2 text-slate-700">{q.control_used || "—"}</td>
                    <td className="px-2 py-2 text-xs text-slate-600">{q.expected_result || "—"} / {q.observed_result || "—"}</td>
                    <td className="px-2 py-2">
                      {q.passed
                        ? <span className="inline-flex items-center gap-1 font-semibold text-green-800"><CheckCircle2 size={14} /> Pass</span>
                        : <span className="inline-flex items-center gap-1 font-semibold text-red-800"><XCircle size={14} /> Fail</span>}
                    </td>
                    <td className="px-2 py-2 text-xs text-slate-600">{q.performed_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {(item.retired_lots_list || []).length > 0 && (
        <Section title={`Retired lots (${item.retired_lots_list.length})`} testid="reagent-retired-lots"
                 action={
                   <Button size="sm" variant="outline" onClick={() => setShowRetiredLots((v) => !v)}
                           data-testid="toggle-retired-lots" className="h-9 border-slate-300">
                     {showRetiredLots ? "Hide" : "Show"}
                   </Button>
                 }>
          {showRetiredLots ? (
            <ul className="space-y-2">
              {item.retired_lots_list.map((lot) => (
                <li key={lot.id} className="flex flex-wrap items-center gap-2 border border-slate-200 p-2 text-sm"
                    data-testid={`retired-lot-${lot.id}`}>
                  <span className="font-mono font-semibold text-slate-800">Lot {lot.lot_number}</span>
                  <span className="text-xs text-slate-500">
                    voided by {lot.voided_by} {fmtDateTime(lot.voided_at)} — {lot.void_reason}
                  </span>
                  {canBench && (
                    <Button size="sm" variant="outline" data-testid={`restore-lot-${lot.id}`} className="ml-auto h-9 border-slate-300"
                            onClick={async () => {
                              try {
                                await api.post(`/reagent-lots/${lot.id}/restore`);
                                toast.success(`Lot ${lot.lot_number} reinstated`);
                                load();
                              } catch (e) { toast.error(apiError(e)); }
                            }}>
                      <Undo2 size={14} className="mr-1" /> Reinstate
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Hidden from the working list — click Show to review them.</p>
          )}
        </Section>
      )}

      <EditDialog
        open={editOpen} onOpenChange={setEditOpen} testid="correct-reagent-dialog"
        title={`Correct ${item.name}`} values={item}
        fields={[
          { name: "name", label: "Reagent name", span: 2 },
          { name: "category", label: "Category", type: "select", options: meta?.categories || [] },
          { name: "department", label: "Department", type: "select", options: meta?.departments || [] },
          { name: "manufacturer", label: "Manufacturer" },
          { name: "catalog_number", label: "Catalog number" },
          { name: "unit", label: "Unit", type: "select", options: meta?.units || [] },
          { name: "storage", label: "Storage", type: "select", options: meta?.storage_conditions || [] },
          { name: "hazard_class", label: "Hazard class", type: "select", options: meta?.hazard_classes || [] },
          { name: "reorder_level", label: "Reorder level", type: "number" },
          { name: "qc_interval_days", label: "Recurring QC interval (days)", type: "number" },
          { name: "notes", label: "Notes", type: "textarea", span: 2 },
        ]}
        onSave={async (body) => {
          await api.put(`/reagents/${id}`, body);
          toast.success("Reagent corrected");
          load();
        }}
      />

      <EditDialog
        open={!!editLot} onOpenChange={(v) => !v && setEditLot(null)} testid="correct-lot-dialog"
        title={`Correct lot ${editLot?.lot_number || ""}`}
        description="Fix a mistyped lot number, expiry or quantity. QC records stay attached to this lot."
        values={editLot || {}}
        fields={[
          { name: "lot_number", label: "Lot number" },
          { name: "expiry_date", label: "Manufacturer expiry", type: "date" },
          { name: "quantity_received", label: "Quantity received", type: "number" },
          { name: "quantity_on_hand", label: "Quantity on hand", type: "number" },
          { name: "received_date", label: "Received date", type: "date" },
          { name: "location", label: "Storage location" },
          { name: "notes", label: "Notes", type: "textarea", span: 2 },
        ]}
        onSave={async (body) => {
          await api.put(`/reagent-lots/${editLot.id}`, body);
          toast.success("Lot corrected");
          load();
        }}
      />

      <RetireDialog
        open={retireOpen} onOpenChange={setRetireOpen} testid="retire-reagent-dialog"
        title={`Retire ${item.name}`} confirmLabel="Retire reagent"
        onConfirm={async (reason) => {
          await api.post(`/reagents/${id}/void`, { reason });
          toast.success("Reagent retired");
          load();
        }}
      />

      <RetireDialog
        open={!!voidLot} onOpenChange={(v) => !v && setVoidLot(null)} testid="void-lot-dialog"
        title={`Void lot ${voidLot?.lot_number || ""}`} confirmLabel="Void lot"
        description="Use this when a lot was received in error or duplicated. Its QC history stays on file."
        onConfirm={async (reason) => {
          await api.post(`/reagent-lots/${voidLot.id}/void`, { reason });
          toast.success("Lot voided");
          load();
        }}
      />

      <Dialog open={!!qcLot} onOpenChange={(v) => !v && setQcLot(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="qc-dialog">
          <DialogHeader>
            <DialogTitle>Record QC — lot {qcLot?.lot_number}</DialogTitle>
            <DialogDescription>A failed QC immediately quarantines this lot so nobody uses it on patient material.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveQc} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">QC type</Label>
              <select value={qcForm.qc_type} onChange={(e) => setQcForm((f) => ({ ...f, qc_type: e.target.value }))}
                      data-testid="qc-type-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                {(meta?.qc_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Control / tissue used</Label>
              <Input value={qcForm.control_used} onChange={(e) => setQcForm((f) => ({ ...f, control_used: e.target.value }))}
                     data-testid="qc-control-input" placeholder="e.g. Tonsil control block" className="mt-1 h-11 border-slate-300" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Expected result</Label>
                <Input value={qcForm.expected_result} onChange={(e) => setQcForm((f) => ({ ...f, expected_result: e.target.value }))}
                       data-testid="qc-expected-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Observed result</Label>
                <Input value={qcForm.observed_result} onChange={(e) => setQcForm((f) => ({ ...f, observed_result: e.target.value }))}
                       data-testid="qc-observed-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setQcForm((f) => ({ ...f, passed: true }))}
                      data-testid="qc-pass-button"
                      className={`h-11 flex-1 border text-sm font-semibold ${qcForm.passed ? "border-green-700 bg-green-50 text-green-900" : "border-slate-300 text-slate-600"}`}>
                Pass
              </button>
              <button type="button" onClick={() => setQcForm((f) => ({ ...f, passed: false }))}
                      data-testid="qc-fail-button"
                      className={`h-11 flex-1 border text-sm font-semibold ${!qcForm.passed ? "border-red-700 bg-red-50 text-red-900" : "border-slate-300 text-slate-600"}`}>
                Fail — quarantine lot
              </button>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Comments</Label>
              <Textarea value={qcForm.comments} onChange={(e) => setQcForm((f) => ({ ...f, comments: e.target.value }))}
                        data-testid="qc-comments-input" className="mt-1 min-h-[70px] border-slate-300" />
            </div>
            <Button type="submit" disabled={busy === "qc"} data-testid="save-qc-button"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "qc" ? "Saving…" : "Save QC record"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      {viewerElement}
    </div>
  );
}
